import { flow, getRoot, types } from 'mobx-state-tree'
import { ContentMetadata } from '../UserQuiz'
import { SurveyPlayQuestion } from './SurveyPlayQuestion'
import { getType, Instance, isAlive, isStateTreeNode } from 'mobx-state-tree'

export const isSurveyPlayQuestion = (item: any): item is Instance<typeof SurveyPlayQuestion> => {
  return item && isStateTreeNode(item) && getType(item).name === 'SurveyPlayQuestion'
}

/**
 * Top-level survey play model. Mirrors `GameQuiz` (activeItem reference, activeIdx,
 * next(), batched submit) so it can drive the shared `QuizItemList` UI, but has no
 * scoring/timing/feedback. Answers are serialized in `responses` using the original
 * snake_case `survey_type` and submitted in one batch when the last item is answered.
 */
export const SurveyPlay = types
  .model('SurveyPlay')
  .props({
    status: types.optional(types.enumeration(['idle', 'pending', 'done', 'error']), 'idle'),
    id: types.identifierNumber,
    title: types.string,
    user_id: types.maybe(types.number),
    is_anonymous: types.optional(types.boolean, false),
    metadata: types.maybeNull(ContentMetadata),
    items: types.array(SurveyPlayQuestion),
    activeItem: types.maybe(types.reference(SurveyPlayQuestion)),
    completed: types.maybe(types.boolean),
  })
  // Handle for the pending question-advance timer (see `next`), kept off the
  // snapshot so it can be cleared on unmount without persisting.
  .volatile(() => ({
    advanceTimer: undefined as ReturnType<typeof setTimeout> | undefined,
  }))
  .actions((self) => ({
    setStatus(value?: 'idle' | 'pending' | 'done' | 'error') {
      self.status = value || 'idle'
    },
    setActiveItem(reference: typeof self.activeItem) {
      self.activeItem = reference
    },
    setCompleted(completed: boolean) {
      self.completed = completed
    },
  }))
  .views((self) => ({
    get loading() {
      return self.status === 'pending'
    },
    get activeIdx() {
      if (self.activeItem) {
        return self.items.indexOf(self.activeItem)
      }
      return 0
    },
    // Serialized answers matching SurveyResponseModel — note question_type is the
    // original snake_case survey_type expected by the API.
    get responses() {
      const responses = [] as any[]
      self.items.forEach((item) => {
        if (item.answered) {
          responses.push({
            question_id: item.id,
            question_type: item.survey_type,
            choice_ids: item.choices.filter((c) => c.selected).map((c) => c.id),
            text_value: item.survey_type === 'open_text' ? item.userText ?? null : null,
            range_value: item.survey_type === 'range_response' ? item.rangeValue ?? null : null,
          })
        }
      })
      return responses
    },
    get rootStore(): any {
      return getRoot(self)
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.activeItem = self.items[0]
    },
    submit: flow(function* () {
      self.setStatus('pending')
      try {
        const { surveyStore, userStore } = self.rootStore
        const payload = {
          survey_id: self.id,
          user_id: self.user_id ?? userStore?.user?.id,
          is_anonymous: self.is_anonymous,
          responses: self.responses,
          metadata: self.metadata ?? undefined,
        }
        // Reuse the store flow so the surveys list cache is invalidated on success.
        yield surveyStore.postSubmitSurveyResponses(payload as any)
        self.setStatus('done')
      } catch {
        self.setStatus('error')
      }
    }),
    // Cancel a pending advance so a torn-down screen can't mutate the tree.
    cancelAdvance() {
      if (self.advanceTimer) {
        clearTimeout(self.advanceTimer)
        self.advanceTimer = undefined
      }
    },
    // Runs when the advance timer fires. Guarded against a play tree that has been
    // detached or already advanced (e.g. the user navigated away within the delay).
    advance(active: Instance<typeof SurveyPlayQuestion>) {
      self.advanceTimer = undefined
      if (!isAlive(self) || self.activeItem !== active) {
        return
      }
      const idx = self.items.indexOf(active)
      if (self.items[idx + 1]) {
        self.setActiveItem(self.items[idx + 1])
      } else {
        this.submit().then(() => {
          if (isAlive(self)) {
            self.setCompleted(true)
          }
        })
      }
    },
    next() {
      const active = self.activeItem
      if (!active) {
        return
      }
      // Brief delay lets the slide transition play; no scoring pause like quizzes.
      // Replace any in-flight timer so rapid taps can't enqueue overlapping advances.
      this.cancelAdvance()
      self.advanceTimer = setTimeout(() => this.advance(active), 400)
    },
  }))
